import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { MANIFESTO, PROFILE } from "@/data/content";

function useCounter(target, inView) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const duration = 1600;
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.floor(eased * target));
      if (p < 1) raf = requestAnimationFrame(step);
      else setValue(target);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);
  return value;
}

function StatCard({ label, value, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const v = useCounter(value, inView);
  return (
    <div
      ref={ref}
      className="glass rounded-2xl p-6 hover:-translate-y-1 hover:border-ink/20 transition-[transform,border-color] duration-300"
      data-testid={`about-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="font-display text-5xl md:text-6xl tracking-tight text-ink">
        {v.toLocaleString()}
        <span className="text-accent-cyan">{suffix}</span>
      </div>
      <p className="mt-2 text-ink/50 text-sm">{label}</p>
    </div>
  );
}

export default function About() {
  const ref = useRef(null);
  return (
    <section
      id="about"
      ref={ref}
      className="relative py-28 md:py-36"
      data-testid="about-section"
    >
      <div className="section-x mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="section-eyebrow">01 — About Me</p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
              Building QA leaders, <br className="hidden md:block" />
              <span className="text-ink/50">one team at a time.</span>
            </h2>
            <p className="mt-6 text-ink/80 text-base md:text-[17px] leading-relaxed max-w-lg">
              I&apos;m Chirag — a QA Automation Architect and SDET Instructor
              with 12+ years of industry experience. I help engineers and
              enterprises build reliable software through modern test
              automation, from Selenium and Playwright to AI-assisted testing
              and Agentic AI.
            </p>

            <ul className="mt-8 grid grid-cols-1 gap-2 max-w-md">
              {[
                "12+ Years Experience in Software Testing",
                "QA Automation Architect",
                "SDET Instructor",
                "Corporate Trainer",
                "Mentor to 5,000+ Engineers",
                "Author of 2 books on Java",
              ].map((s) => (
                <li
                  key={s}
                  className="flex items-center gap-3 text-ink/75 text-sm"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-accent-cyan shadow-[0_0_10px_rgba(124,92,246,0.6)]" />
                  {s}
                </li>
              ))}
            </ul>

            <div className="mt-10 grid grid-cols-2 gap-4">
              {PROFILE.stats.slice(0, 4).map((s) => (
                <StatCard key={s.label} {...s} />
              ))}
            </div>
          </div>

          {/* Manifesto chapters */}
          <div className="lg:col-span-7 lg:pl-8">
            <p className="text-[11px] uppercase tracking-[0.32em] text-ink/55 font-mono">
              Manifesto
            </p>
            <div className="mt-6 space-y-8">
              {MANIFESTO.map((m, i) => (
                <ManifestoRow key={m.n} chapter={m} index={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ManifestoRow({ chapter, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.08 }}
      className="grid grid-cols-[auto_1fr] gap-6 md:gap-10 items-start border-t border-ink/[0.08] pt-8"
      data-testid={`manifesto-${chapter.n}`}
    >
      <span className="font-display text-[80px] md:text-[120px] leading-none text-ink/[0.06] select-none">
        {chapter.n}
      </span>
      <div className="pt-4 md:pt-6">
        <h3 className="font-display text-2xl md:text-3xl tracking-tight">
          {chapter.title}
        </h3>
        <p className="mt-3 text-ink/60 text-base md:text-[17px] leading-relaxed max-w-xl">
          {chapter.body}
        </p>
      </div>
    </motion.div>
  );
}
